import {
  DateRange,
  getCsvExportUrl,
  type ContactRequestParams,
} from '@/api/ubmContactRequestCsvExportService';
import { ComposerTranslation } from 'vue-i18n';

export enum TimeframeOptions {
  'ALL' = 'all',
  '7D' = 'last7Days',
  '30D' = 'last30days',
  '3M' = 'last3months',
  'CUSTOM' = 'custom',
}

export const getDateRangeFromTimeframe = (selectedTimeframe: TimeframeOptions): DateRange => {
  if (selectedTimeframe === TimeframeOptions.ALL) {
    return {};
  }

  // Floor to the day
  const start = new Date();
  start.setHours(0, 0, 0, 0);

  switch (selectedTimeframe) {
    case TimeframeOptions['3M']:
      start.setUTCFullYear(start.getUTCFullYear(), start.getUTCMonth() - 3);
      break;
    case TimeframeOptions['30D']:
      // Setting 30 days to "same day but in last month"
      start.setUTCFullYear(start.getUTCFullYear(), start.getUTCMonth() - 1);
      break;
    case TimeframeOptions['7D']:
      start.setUTCFullYear(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() - 7);
      break;
    default:
  }

  return {
    startDate: start.toISOString(),
  };
};

export async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  try {
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
  } finally {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

const FILENAME_CONTENT_DISPOSITION_REGEX = /filename="([^"]+)"/;

export function extractFilename(translatedFilenamePrefix: string, url: URL): string {
  const defaultFilename = `${translatedFilenamePrefix}.csv`;

  const contentDisposition = url.searchParams.get('response-content-disposition');
  if (!contentDisposition) return defaultFilename;

  const filenameMatch = contentDisposition.match(FILENAME_CONTENT_DISPOSITION_REGEX);
  if (!filenameMatch || !filenameMatch[1]) return defaultFilename;

  return filenameMatch[1].replace('contact-requests', translatedFilenamePrefix);
}

const INVALID_ADDRESS_CHARACTERS = /[^\w\d]+/g;
const COMBINING_MARKS_REGEX = /[\u0300-\u036f]/g; // SEE https://en.wikipedia.org/wiki/List_of_Unicode_characters#Combining_marks

export function sanitizeAddress(address: string): string {
  return address
    .toLowerCase()
    .replaceAll('ä', 'ae')
    .replaceAll('ö', 'oe')
    .replaceAll('ü', 'ue')
    .normalize('NFD')
    .replace(COMBINING_MARKS_REGEX, '')
    .replaceAll(INVALID_ADDRESS_CHARACTERS, '_');
}

export function formatDate(dateStr: string): string {
  const date = new Date(Date.parse(dateStr));

  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
}

const MULTIPLE_UNDERSCORE_REGEX = /_{2,}/g;

const generateFilename = (t: ComposerTranslation, params: ContactRequestParams): string => {
  const filenameParts = [t('csvExport.leads').toLowerCase()];

  if (params.address) {
    filenameParts.push(sanitizeAddress(params.address));
  }

  if (params.startDate) {
    filenameParts.push(formatDate(params.startDate));
  }

  filenameParts.push(t('csvExport.to').toLowerCase());
  filenameParts.push(formatDate(params.endDate || new Date().toISOString()));

  return (
    filenameParts.join('_').replaceAll(MULTIPLE_UNDERSCORE_REGEX, '_') + `.${params.fileFormat}`
  );
};

export async function fetchAndDownloadLeadsExport(
  t: ComposerTranslation,
  params: ContactRequestParams,
): Promise<void> {
  try {
    const responseData = await getCsvExportUrl(params);
    if (!responseData?.url) throw new Error('Export URL not found in response');

    const csvResponse = await fetch(responseData.url);
    if (!csvResponse.ok) throw new Error(`Failed to fetch CSV data: ${csvResponse.status}`);

    const blob = await csvResponse.blob();
    if (!blob.size) throw new Error('Received empty CSV data');

    await downloadBlob(blob, generateFilename(t, params));
  } catch (error) {
    throw new Error(
      `Failed to export leads: ${error instanceof Error ? error.message : 'Unknown error'}`,
      { cause: error },
    );
  }
}
